import { Component } from '@theme/component';

/**
 * FH: scroll progress bar for the resource-list carousels, porting the old theme's
 * `.swiper-pagination-progressbar`.
 *
 * Horizon's slideshow ships dots, a counter and thumbnails as pagination — all of which
 * assume one slide at a time, so none fit a carousel showing several product cards at once.
 *
 * This reads the scroller's own geometry rather than the slideshow's current slide index:
 * the fill rests at the share of the list that fits on screen (the same fraction Swiper
 * started at) and reaches 1 at the end. Measuring the scroller keeps it in step with drag,
 * wheel, keyboard and arrow scrolling alike, and correct on first paint and after a resize,
 * without hooking into the slideshow's internals.
 *
 * Publishes the fraction as `--fh-carousel-progress` on itself; the bar is drawn in
 * snippets/fh-carousel-controls.liquid.
 */
class FhCarouselProgress extends Component {
  /**
   * The slideshow's scroll container.
   * @type {HTMLElement | null}
   */
  #scroller = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const scroller = this.closest('slideshow-component')?.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    this.#scroller = scroller;
    scroller.addEventListener('scroll', this.#update, { passive: true });

    // Slide widths are relative to the container, so a resize changes how much fits on
    // screen — and therefore where the bar rests. Observing also gives us the first
    // measurement, once the slides have been laid out.
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(scroller);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const { scrollWidth, clientWidth, scrollLeft } = scroller;

    // Not laid out yet — a collapsed ancestor, or a section re-rendered off-screen. Leave
    // the server-rendered state alone rather than latching "nothing to scroll" off a
    // zero-width measurement: with the row hidden nothing scrolls, so no later scroll
    // event would come along to correct it.
    if (clientWidth === 0) return;

    const scrollable = scrollWidth - clientWidth;

    // Sub-pixel slack: slide widths are fractional, so `scrollWidth` can exceed
    // `clientWidth` by a rounding error even when everything already fits.
    const inert = scrollable <= 1;
    this.toggleAttribute('data-fh-inert', inert);
    if (inert) return;

    const atRest = clientWidth / scrollWidth;
    // `scrollLeft` runs negative in RTL.
    const progress = Math.min(Math.abs(scrollLeft) / scrollable, 1);

    this.style.setProperty('--fh-carousel-progress', `${atRest + (1 - atRest) * progress}`);
  };
}

if (!customElements.get('fh-carousel-progress')) {
  customElements.define('fh-carousel-progress', FhCarouselProgress);
}
